import { Tab } from '@headlessui/react';
import Badge from '@v2/shared/components/Badge';
import { Category, MenuItem } from '@v2/shared/lib/types';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { FullScreenDialog } from '@v2/brands/discountmags/Layouts/Full/Navigation';

import BooksMenu from './BooksMenu';
import AboutUsPanel from './AboutUsPanel';
import MagazinesMenu from './MagazinesMenu';
import ConnectWithUsPanel from './ConnectWithUsPanel';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  categories: Category[];
  featuredCategories: Category[];
  booksCategories?: Category[];
  featuredBooksCategories?: Category[];
  specialDealLinks: MenuItem[];
  socialLinks: MenuItem[];
};

export default function MainMenu({
  isOpen,
  close,
  toggle,
  categories,
  featuredCategories,
  booksCategories,
  featuredBooksCategories,
  specialDealLinks,
  socialLinks,
}: Props) {
  const tabClasses = cn(
    'font-semibold text-lg pb-1',
    'text-slate-800 ui-selected:text-red-600',
    'border-b-2 border-transparent ui-selected:border-red-600'
  );
  const { buildUrl } = useUrlGenerator();
  // Gifts & More is one page, so its tab is a link straight to it rather than a panel.
  // US-only, on the same terms as the footer's: RoutesBuilder registers the page for
  // DiscountMags US alone.
  const { country } = useSite();
  const hasGiftsPage = country === 'US';

  return (
    <>
      <FullScreenDialog slideInFrom="left" close={close} isOpen={isOpen} toggle={toggle} title="Menu">
        {(!booksCategories || !featuredBooksCategories) && !hasGiftsPage && (
          <MagazinesMenu
            categories={categories}
            featuredCategories={featuredCategories}
            specialDealLinks={specialDealLinks}
            closeMainMenu={close}
          />
        )}

        {((booksCategories && featuredBooksCategories) || hasGiftsPage) && (
          <Tab.Group>
            {/* The link sits beside the tab list rather than in it: a tablist may own
                only tabs, and axe fails aria-required-children on a link inside one. */}
            <div className="pt-4 px-4 flex gap-5">
              <Tab.List className="flex gap-5">
                <Tab className={cn(tabClasses)}>Magazines</Tab>
                {booksCategories && featuredBooksCategories && <Tab className={cn(tabClasses, 'relative')}>Books</Tab>}
              </Tab.List>
              {hasGiftsPage && (
                <a href={buildUrl('/categories/gifts')} className={tabClasses}>
                  Gifts &amp; More
                </a>
              )}
            </div>
            <Tab.Panels>
              <Tab.Panel>
                <MagazinesMenu
                  categories={categories}
                  featuredCategories={featuredCategories}
                  specialDealLinks={specialDealLinks}
                  closeMainMenu={close}
                />
              </Tab.Panel>
              {booksCategories && featuredBooksCategories && (
                <Tab.Panel>
                  <BooksMenu
                    categories={booksCategories}
                    featuredCategories={featuredBooksCategories}
                    closeMainMenu={close}
                  />
                </Tab.Panel>
              )}
            </Tab.Panels>
          </Tab.Group>
        )}

        <AboutUsPanel />
        <ConnectWithUsPanel socialLinks={socialLinks} />
      </FullScreenDialog>
    </>
  );
}
